import { ContentState, EditorState, Modifier, SelectionState } from 'draft-js';
import { CharCounter } from 'lib/components/formik/commonStyles';
import React, { useEffect, useState } from 'react';
import { Typography } from 'react-covideo-common';
import { Editor } from 'react-draft-wysiwyg';
import { MdWarning } from 'react-icons/md';
import styled, { useTheme } from 'styled-components/macro';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

type AiScriptEditorProps = {
  content: string;
  maxLength: number;
  selectedScript: number;
  onTextEditorChange: (text: string) => void;
};

export const AiScriptEditor = ({
  content,
  selectedScript,
  maxLength,
  onTextEditorChange,
}: AiScriptEditorProps) => {
  const isMaxLengthReached = content.length >= maxLength;
  const theme = useTheme();

  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const onEditorStateChange = (currentEditorState: EditorState) => {
    if (!currentEditorState) return;

    const currentContent = currentEditorState.getCurrentContent();
    const text = currentContent.getPlainText();
    onTextEditorChange(text);
    setEditorState(currentEditorState);
  };

  const boldTextBetweenDelimiters = (currentEditorState: EditorState) => {
    const contentState = currentEditorState.getCurrentContent();
    const blocks = contentState.getBlockMap();
    let newContentState = contentState;

    blocks.forEach(block => {
      if (!block) return;
      const text = block.getText();
      const regex = /\[(.*?)\]/g; // match text inside []
      let match;

      // search all regex matches
      while ((match = regex.exec(text)) !== null) {
        const start = match.index;
        const end = regex.lastIndex;

        // bold brackets + text inside
        newContentState = Modifier.applyInlineStyle(
          newContentState,
          new SelectionState({
            anchorKey: block.getKey(),
            anchorOffset: start,
            focusKey: block.getKey(),
            focusOffset: end,
          }),
          'BOLD'
        );
      }
    });

    const newEditorState = EditorState.moveFocusToEnd(currentEditorState);
    return EditorState.push(
      newEditorState,
      newContentState,
      'change-inline-style'
    );
  };

  useEffect(() => {
    if (!!editorState) {
      const valueInEditor = editorState.getCurrentContent().getPlainText();
      // do not update editor state if same as content
      if (valueInEditor === content) return;
    }

    const currentEditorState = EditorState.createWithContent(
      ContentState.createFromText(content)
    );
    const boldEditorState = boldTextBetweenDelimiters(currentEditorState);
    setEditorState(boldEditorState);
  }, [content]);

  useEffect(() => {
    if (!selectedScript) {
      setEditorState(EditorState.createEmpty());
    }
  }, [selectedScript]);

  return (
    <Container>
      <Editor
        toolbar={{
          options: [],
        }}
        editorStyle={{
          width: '100%',
          height: 'calc(100% - 30px)',
          maxHeight: 'unset',
        }}
        wrapperStyle={{
          resize: 'vertical',
          overflow: 'auto',
          height: '195px',
          width: '100%',

          borderRadius: '8px',
          border: '1px solid #D0D3D9',
          padding: 12,
          fontFamily: 'Verdana',
          boxSizing: 'border-box',
        }}
        toolbarHidden
        editorState={editorState}
        onEditorStateChange={onEditorStateChange}
      />

      <CharCounter isMaxLengthReached={isMaxLengthReached}>
        {isMaxLengthReached && (
          <MdWarning
            color={theme.colors.danger[100]}
            size={14}
            style={{ marginRight: '0px' }}
          />
        )}
        <Typography variant='extra-small'>
          {content.length}/{maxLength}
        </Typography>
      </CharCounter>
    </Container>
  );
};
